import type { TranscriptUpdate } from "@frontierx/shared/schemas/TranscriptUpdate";
import { updateTranscriptArray } from "@frontierx/shared/utils/updateTranscript";
// Define ScriptItem type locally
import type {
  AILatencyMetrics,
  AIQuestionAnswer,
  AIStreamingStatus,
  CallQuestion,
  PartialAIResponse,
  PlaybookProgress,
  PlaybookScriptItem,
  PlaybookSection,
  RecallBot,
  RecallMeetingParticipant,
  StatusChange,
} from "@frontierx/types";
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import type { CompatibleSocket } from "~/app/types/CompatibleSocket";
import type { HandleGetHelpFunctionArgs } from "../components/Help/AiHelpCard";

// Export the BotSocket type for components that need it
export type BotSocketType = Partial<CompatibleSocket> & {
  send: (message: string) => void;
  close: () => void;
  reconnect: () => void;
};

// Define types locally for now
interface BotInfo {
  id: string;
  status: string;
  meetingParticipants?: number;
}

export type PartyServerConnectionStatus =
  | "connected"
  | "disconnected"
  | "reconnecting";

interface CallState {
  // Connection state
  isConnected: boolean;
  connectionStatus:
    | "connecting"
    | "connected"
    | "disconnected"
    | "reconnecting";
  reconnectAttempt: number;
  partyServerConnectionStatus: PartyServerConnectionStatus;

  // Bot state
  botInfo: BotInfo | null;
  botStatus: string | null;
  roomBot: RecallBot | null;

  // Playbook state
  playbook: PlaybookProgress;
  currentScriptItem: PlaybookScriptItem | undefined;

  // Transcript and participants
  transcript: TranscriptUpdate[];
  participants: RecallMeetingParticipant[];

  // AI state
  aiHistory: AIQuestionAnswer[];
  questions: CallQuestion[];
  error: string | null;
  streamingResponse: PartialAIResponse | null;
  isStreaming: boolean;
  streamingStatus: AIStreamingStatus | undefined;
  currentAiHelpRequest: HandleGetHelpFunctionArgs | undefined;
  messageLatency: number;
  latencyMetrics: AILatencyMetrics | undefined;

  // Participant roles
  participantRoles: {
    assignments: Array<{
      recallParticipantId: string;
      role: "salesperson" | "client" | "observer";
      displayName: string;
      isCurrentUser?: boolean;
      assignedBy?: string;
      assignedAt?: string;
    }>;
    participants: Array<{
      id: number;
      name: string;
      role: "salesperson" | "client" | "observer";
      isHost: boolean;
      hasExplicitAssignment: boolean;
      assignedAt?: string;
    }>;
  } | null;

  // Script detection logs
  scriptDetectionLogs: Array<{
    timestamp: number;
    scriptItemId: string;
    scriptItemText: string;
    triggeringText: string;
    checksPerformed: Array<{
      method: string;
      score: number;
      partialThreshold: number;
      fullThreshold: number;
    }>;
    decision: "completed" | "partial" | "no_match";
    reason: string;
    similarity?: number;
  }>;

  // UI state
  isThrashing: boolean;
  shouldSuggestRefresh: boolean;
  thrashingMessage: string | null;

  // Actions
  setConnectionStatus: (status: CallState["connectionStatus"]) => void;
  setConnected: (connected: boolean) => void;
  incrementReconnectAttempt: () => void;
  resetReconnectAttempt: () => void;
  setPartyServerConnectionStatus: (status: PartyServerConnectionStatus) => void;

  setBotInfo: (botInfo: BotInfo | null) => void;
  setBotStatus: (status: string | null) => void;
  setRoomBot: (roomBot: RecallBot | null) => void;
  updateRoomBotStatus: (status: StatusChange) => void;

  setPlaybook: (playbook: PlaybookProgress) => void;
  setPlaybookCurrentSection: (sectionId: string | null) => void;
  updatePlaybookSection: (section: PlaybookSection) => void;
  setCurrentScriptItem: (item: PlaybookScriptItem | undefined) => void;

  setTranscript: (transcript: TranscriptUpdate[]) => void;
  addTranscriptItem: (item: TranscriptUpdate) => void;
  updateTranscriptItem: (item: TranscriptUpdate) => void;
  setParticipants: (participants: RecallMeetingParticipant[]) => void;

  setAIHistory: (history: AIQuestionAnswer[]) => void;
  addAIHistoryItem: (item: AIQuestionAnswer) => void;
  setQuestions: (questions: CallQuestion[]) => void;
  addQuestion: (question: CallQuestion) => void;
  setError: (error: string | null) => void;
  clearError: () => void;

  setStreamingResponse: (response: PartialAIResponse | null) => void;
  setIsStreaming: (isStreaming: boolean) => void;
  setStreamingStatus: (status: AIStreamingStatus | undefined) => void;
  setCurrentAiHelpRequest: (
    request: HandleGetHelpFunctionArgs | undefined,
  ) => void;
  setMessageLatency: (latency: number) => void;
  setLatencyMetrics: (metrics: AILatencyMetrics | undefined) => void;

  // AI streaming management
  endAIStream: (finalResponse?: PartialAIResponse) => void;
  handleAIError: () => void;

  setParticipantRoles: (roles: CallState["participantRoles"]) => void;
  setScriptDetectionLogs: (logs: CallState["scriptDetectionLogs"]) => void;
  addScriptDetectionLog: (log: CallState["scriptDetectionLogs"][0]) => void;

  setThrashing: (isThrashing: boolean, message?: string) => void;
  setShouldSuggestRefresh: (shouldSuggest: boolean) => void;

  reset: () => void;
}

const initialState = {
  isConnected: false,
  connectionStatus: "disconnected" as const,
  reconnectAttempt: 0,
  partyServerConnectionStatus: "disconnected" as const,
  botInfo: null,
  botStatus: null,
  roomBot: null,
  playbook: {
    sections: [],
    currentSectionId: null,
    status: "in_progress",
  } as PlaybookProgress,
  currentScriptItem: undefined,
  transcript: [],
  participants: [],
  aiHistory: [],
  questions: [],
  error: null,
  streamingResponse: null,
  isStreaming: false,
  streamingStatus: undefined,
  currentAiHelpRequest: undefined,
  messageLatency: 0,
  latencyMetrics: undefined,
  participantRoles: null,
  scriptDetectionLogs: [],
  isThrashing: false,
  shouldSuggestRefresh: false,
  thrashingMessage: null,
};

export const useCallStore = create<CallState>()(
  devtools(
    (set, _get) => ({
      ...initialState,

      // Connection actions
      setConnectionStatus: (status) =>
        set({ connectionStatus: status }, false, "setConnectionStatus"),

      setConnected: (connected) =>
        set({ isConnected: connected }, false, "setConnected"),

      incrementReconnectAttempt: () =>
        set(
          (state) => ({ reconnectAttempt: state.reconnectAttempt + 1 }),
          false,
          "incrementReconnectAttempt",
        ),

      resetReconnectAttempt: () =>
        set({ reconnectAttempt: 0 }, false, "resetReconnectAttempt"),

      setPartyServerConnectionStatus: (status) =>
        set(
          { partyServerConnectionStatus: status },
          false,
          "setPartyServerConnectionStatus",
        ),

      // Bot actions
      setBotInfo: (botInfo) => set({ botInfo }, false, "setBotInfo"),

      setBotStatus: (status) =>
        set({ botStatus: status }, false, "setBotStatus"),

      setRoomBot: (roomBot) => {
        console.log("[CallStore] Setting roomBot:", {
          hasRoomBot: !!roomBot,
          botId: roomBot?.id,
          statusChanges: roomBot?.status_changes?.length || 0,
          latestStatus:
            roomBot?.status_changes?.[roomBot.status_changes.length - 1]?.code,
        });
        set({ roomBot }, false, "setRoomBot");
      },

      updateRoomBotStatus: (status) =>
        set(
          (state) => {
            if (!state.roomBot) return state;
            return {
              roomBot: {
                ...state.roomBot,
                status_changes: [
                  ...(state.roomBot.status_changes ?? []),
                  status,
                ],
              },
            };
          },
          false,
          "updateRoomBotStatus",
        ),

      // Playbook actions
      setPlaybook: (playbook) => {
        console.log("[CallStore] Setting playbook:", {
          hasPlaybook: !!playbook,
          sections: playbook?.sections?.length || 0,
          currentSectionId: playbook?.currentSectionId,
        });
        set({ playbook }, false, "setPlaybook");
      },

      setPlaybookCurrentSection: (sectionId) =>
        set(
          (state) => ({
            playbook: { ...state.playbook, currentSectionId: sectionId },
          }),
          false,
          "setPlaybookCurrentSection",
        ),

      updatePlaybookSection: (updatedSection) =>
        set(
          (state) => ({
            playbook: {
              ...state.playbook,
              sections: state.playbook.sections.map((section) =>
                section.id === updatedSection.id ? updatedSection : section,
              ),
            },
          }),
          false,
          "updatePlaybookSection",
        ),

      setCurrentScriptItem: (item) => {
        console.log("[CallStore] Setting current script item:", {
          hasItem: !!item,
          itemId: item?.id,
          itemText: `${item?.text?.substring(0, 50)}...`,
        });
        set({ currentScriptItem: item }, false, "setCurrentScriptItem");
      },

      // Transcript and participants actions
      setTranscript: (transcript) =>
        set({ transcript }, false, "setTranscript"),

      addTranscriptItem: (item) =>
        set(
          (state) => ({ transcript: [...state.transcript, item] }),
          false,
          "addTranscriptItem",
        ),

      updateTranscriptItem: (item) =>
        set(
          (state) => ({
            transcript: updateTranscriptArray(state.transcript, item),
          }),
          false,
          "updateTranscriptItem",
        ),

      setParticipants: (participants) =>
        set({ participants }, false, "setParticipants"),

      // AI actions
      setAIHistory: (aiHistory) => set({ aiHistory }, false, "setAIHistory"),

      addAIHistoryItem: (item) =>
        set(
          (state) => ({ aiHistory: [...state.aiHistory, item] }),
          false,
          "addAIHistoryItem",
        ),

      setQuestions: (questions) => set({ questions }, false, "setQuestions"),

      addQuestion: (question) =>
        set(
          (state) => ({ questions: [...state.questions, question] }),
          false,
          "addQuestion",
        ),

      setError: (error) => set({ error }, false, "setError"),

      clearError: () => set({ error: null }, false, "clearError"),

      setStreamingResponse: (streamingResponse) =>
        set({ streamingResponse }, false, "setStreamingResponse"),

      setIsStreaming: (isStreaming) =>
        set({ isStreaming }, false, "setIsStreaming"),

      setStreamingStatus: (streamingStatus) =>
        set({ streamingStatus }, false, "setStreamingStatus"),

      setCurrentAiHelpRequest: (currentAiHelpRequest) =>
        set({ currentAiHelpRequest }, false, "setCurrentAiHelpRequest"),

      setMessageLatency: (messageLatency) =>
        set({ messageLatency }, false, "setMessageLatency"),

      setLatencyMetrics: (latencyMetrics) =>
        set({ latencyMetrics }, false, "setLatencyMetrics"),

      // AI streaming management actions
      endAIStream: (finalResponse) =>
        set(
          {
            isStreaming: false,
            streamingResponse: finalResponse || null,
            streamingStatus: undefined,
            currentAiHelpRequest: undefined,
          },
          false,
          "endAIStream",
        ),

      handleAIError: () =>
        set(
          {
            isStreaming: false,
            streamingStatus: undefined,
            currentAiHelpRequest: undefined,
            streamingResponse: null,
          },
          false,
          "handleAIError",
        ),

      // Participant roles actions
      setParticipantRoles: (participantRoles) =>
        set({ participantRoles }, false, "setParticipantRoles"),

      // Script detection actions
      setScriptDetectionLogs: (scriptDetectionLogs) =>
        set({ scriptDetectionLogs }, false, "setScriptDetectionLogs"),

      addScriptDetectionLog: (log) =>
        set(
          (state) => ({
            scriptDetectionLogs: [...state.scriptDetectionLogs, log].slice(
              -100,
            ), // Keep last 100
          }),
          false,
          "addScriptDetectionLog",
        ),

      // Thrashing actions
      setThrashing: (isThrashing, message?: string) =>
        set(
          { isThrashing, thrashingMessage: message || null },
          false,
          "setThrashing",
        ),

      setShouldSuggestRefresh: (should) =>
        set({ shouldSuggestRefresh: should }, false, "setShouldSuggestRefresh"),

      // Reset
      reset: () => {
        console.log("[CallStore] Resetting all state");
        set(initialState, false, "reset");
      },
    }),
    {
      name: "call-store", // DevTools name
    },
  ),
);

// Selectors for common state combinations - Individual selectors to avoid infinite loops
export const usePlaybook = () => useCallStore((state) => state.playbook);
export const useCurrentScriptItem = () =>
  useCallStore((state) => state.currentScriptItem);
export const useSetPlaybook = () => useCallStore((state) => state.setPlaybook);
export const useSetCurrentScriptItem = () =>
  useCallStore((state) => state.setCurrentScriptItem);

// Bot selectors
export const useRoomBot = () => useCallStore((state) => state.roomBot);
export const useSetRoomBot = () => useCallStore((state) => state.setRoomBot);
export const useBotStatus = () => useCallStore((state) => state.botStatus);
export const useSetBotStatus = () =>
  useCallStore((state) => state.setBotStatus);

// Connection selectors
export const useIsConnected = () => useCallStore((state) => state.isConnected);
export const useConnectionStatus = () =>
  useCallStore((state) => state.connectionStatus);
export const useReconnectAttempt = () =>
  useCallStore((state) => state.reconnectAttempt);
export const usePartyServerConnectionStatus = () =>
  useCallStore((state) => state.partyServerConnectionStatus);
export const useIsThrashing = () => useCallStore((state) => state.isThrashing);
export const useShouldSuggestRefresh = () =>
  useCallStore((state) => state.shouldSuggestRefresh);
export const useThrashingMessage = () =>
  useCallStore((state) => state.thrashingMessage);

// Transcript and participants selectors
export const useTranscript = () => useCallStore((state) => state.transcript);
export const useParticipants = () =>
  useCallStore((state) => state.participants);

// AI selectors
export const useAIHistory = () => useCallStore((state) => state.aiHistory);
export const useQuestions = () => useCallStore((state) => state.questions);
export const useError = () => useCallStore((state) => state.error);
export const useStreamingResponse = () =>
  useCallStore((state) => state.streamingResponse);
export const useIsStreaming = () => useCallStore((state) => state.isStreaming);
export const useStreamingStatus = () =>
  useCallStore((state) => state.streamingStatus);
export const useCurrentAiHelpRequest = () =>
  useCallStore((state) => state.currentAiHelpRequest);
export const useMessageLatency = () =>
  useCallStore((state) => state.messageLatency);
export const useLatencyMetrics = () =>
  useCallStore((state) => state.latencyMetrics);

// Other selectors
export const useParticipantRoles = () =>
  useCallStore((state) => state.participantRoles);
export const useScriptDetectionLogs = () =>
  useCallStore((state) => state.scriptDetectionLogs);
export const useAddScriptDetectionLog = () =>
  useCallStore((state) => state.addScriptDetectionLog);

// Individual bot selectors
export const useBotInfo = () => useCallStore((state) => state.botInfo);
export const useSetBotInfo = () => useCallStore((state) => state.setBotInfo);

// Action selectors
export const useClearError = () => useCallStore((state) => state.clearError);
export const useResetCallStore = () => useCallStore((state) => state.reset);
